import { useMemo } from 'react';
import { Button } from 'antd';
import { ExportOutlined, FileExcelOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import printerIcon from '../../../../../../../img/printer.svg';
import { SaveSearchButton } from '@client/components/SavedSearch';
import ShareButton from '@client/components/ShareButton';
import TutorialButton from '@client/components/ui/TutorialButton';
import useExtractionExport from '@client/feature/treatment/hooks/useExtractionExport';

/**
 * Results header with title, print button, and count display
 * @param {Object} props - Component props
 * @param {Object} props.data - Search results data
 * @param {number} props.data.count - Total number of results
 * @param {string} props.data.query_time - Query execution time
 * @returns {JSX.Element} Results header
 */
/**
 * Map searchMode to the correct saved search type
 */
const SEARCH_MODE_TO_TYPE = {
  rapide: 'treatment_rapide',
  multichoix: 'treatment_multichoix',
  extraction: 'treatment_extraction',
};

const SEARCH_MODE_TO_TUTORIAL_SLOT = {
  rapide: 'treatment-rapide-results',
  multichoix: 'treatment-multichoix-results',
  extraction: 'treatment-extraction-results',
};

const ResultsHeader = ({ data, filters, searchMode = 'rapide', searchData, mergedFilters }) => {
  const { t } = useTranslation();
  const { isExporting, startExport } = useExtractionExport();

  const totalCount = data?.count || 0;

  const searchType = SEARCH_MODE_TO_TYPE[searchMode] || 'treatment_rapide';

  // PFI navigation: show when exactly 1 culture is selected and it has is_pfi=true
  const pfiCultureInfo = useMemo(() => {
    const cultureLabels =
      searchData?.selectionLabels?.['treatment_cultures.culture'] ||
      searchData?.selectionLabels?.cultures ||
      [];

    if (cultureLabels.length !== 1) return null;

    const culture = cultureLabels[0];
    if (!culture?.is_pfi) return null;

    return {
      name: culture.label,
      nameToShowOnIcon: culture.name_to_show_on_icon?.name || culture.name_to_show_on_icon || '',
    };
  }, [searchData?.selectionLabels]);

  // Build PFI URL with current search params
  const pfiURL = useMemo(() => {
    if (!pfiCultureInfo) return null;
    const currentParams = new URLSearchParams(window.location.search);
    // Remove view param (PFI page doesn't use it)
    currentParams.delete('view');
    currentParams.set('mode', 'rapide');
    return `/traitement/pfi/?${currentParams.toString()}`;
  }, [pfiCultureInfo]);

  return (
    <div className="flex flex-col gap-4 mb-6 sm:mb-8">
      {/* PFI tab navigation */}
      {pfiCultureInfo && (
        <div className="flex items-center bg-white rounded-[34px] p-1 w-fit">
          <span className="px-4 py-2 text-sm font-medium text-white bg-[#186845] rounded-[34px] cursor-default">
            {t('pfi-search|results.tab_treatments')}
          </span>
          <a
            href={pfiURL}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-black/65 hover:text-[#186845] rounded-[34px] transition-colors no-underline"
          >
            {t('pfi-search|results.tab_pfi', {
              nameToShowOnIcon: pfiCultureInfo.nameToShowOnIcon,
              cultureName: pfiCultureInfo.name,
            })}
            <ExportOutlined className="text-xs" />
          </a>
        </div>
      )}

      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
        <h4 className="normal-case text-lg sm:text-xl font-medium">
          {t('common|search_results')}
        </h4>

        <div className="flex flex-col gap-2 w-full sm:flex-row sm:w-auto sm:gap-2">
          <TutorialButton slotKey={SEARCH_MODE_TO_TUTORIAL_SLOT[searchMode]} className="w-full sm:w-auto" />
          {searchMode === 'extraction' ? (
            <Button
              type="default"
              variant="outlined"
              size="large"
              loading={isExporting}
              disabled={isExporting || totalCount === 0}
              onClick={() => startExport(mergedFilters, searchData)}
              className="print-button-with-icon w-full sm:w-auto text-sm sm:text-base py-2.5 px-4 sm:py-2 sm:px-3"
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <FileExcelOutlined className="w-4 h-4" />
              {isExporting
                ? t('treatment-search|extraction.export_in_progress')
                : t('treatment-search|extraction.export_excel')}
            </Button>
          ) : (
            <Button
              type="default"
              variant="outlined"
              size="large"
              className="print-button-with-icon w-full sm:w-auto text-sm sm:text-base py-2.5 px-4 sm:py-2 sm:px-3"
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <img src={printerIcon} alt="Print" className="w-4 h-4 print-icon" />
              {t('common|printable_version')}
            </Button>
          )}
          <ShareButton className="w-full sm:w-auto text-sm sm:text-base py-2.5 px-4 sm:py-2 sm:px-3 font-medium" resetKey={filters} />
          <SaveSearchButton
            searchType={searchType}
            getCurrentUrl={() => window.location.href}
          />
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <p className="text-gray-500">
          {totalCount} {t('common|result', 'résultat')}{totalCount > 1 ? 's' : ''}
        </p>
      </div>
    </div>
  );
};

export default ResultsHeader;
